'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { useBranding } from '@/hooks/useBranding';

// SVG icon components for features
const Icons = {
    patients: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
    ),
    ai: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    ),
    prescription: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
    ),
    lab: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
    ),
    inventory: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
    ),
    multitenant: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
    ),
};

const FEATURES = [
    {
        icon: Icons.patients,
        title: 'Quản lý bệnh nhân & EMR',
        desc: 'Tiếp nhận nhanh, hàng đợi thông minh, hồ sơ bệnh án điện tử, lịch sử khám bệnh đầy đủ theo dòng thời gian.',
        gradient: 'from-indigo-500 to-blue-500',
    },
    {
        icon: Icons.ai,
        title: 'AI thông minh',
        desc: 'Nhận diện thuốc từ ảnh, gợi ý kê toa tự động theo chẩn đoán, phân tích đơn hàng bằng AI.',
        gradient: 'from-purple-500 to-pink-500',
    },
    {
        icon: Icons.prescription,
        title: 'Kê toa & Toa mẫu',
        desc: 'Kê toa nhanh với toa mẫu, kiểm tra tương tác thuốc, thay thế thuốc hết hàng tự động.',
        gradient: 'from-emerald-500 to-teal-500',
    },
    {
        icon: Icons.lab,
        title: 'Xét nghiệm & Thiết bị',
        desc: 'Tích hợp trực tiếp máy xét nghiệm (BC-3000), nhận kết quả real-time, quản lý chỉ số lâm sàng.',
        gradient: 'from-amber-500 to-orange-500',
    },
    {
        icon: Icons.inventory,
        title: 'Tồn kho, POS & Hóa đơn',
        desc: 'Quản lý lô hạn FEFO, bán hàng tích hợp, thanh toán QR, xuất hóa đơn điện tử tự động.',
        gradient: 'from-cyan-500 to-blue-500',
    },
    {
        icon: Icons.multitenant,
        title: 'Đa chi nhánh & Báo cáo',
        desc: 'Multi-tenant SaaS, quản lý nhiều phòng khám, tính công bác sĩ, ứng dụng desktop offline.',
        gradient: 'from-rose-500 to-pink-500',
    },
];

const STATS = [
    { value: 'Real-time', label: 'Đồng bộ dữ liệu', icon: '⚡' },
    { value: 'AI', label: 'Hỗ trợ thông minh', icon: '🧠' },
    { value: 'Multi', label: 'Đa nền tảng', icon: '💻' },
    { value: '24/7', label: 'Hoạt động liên tục', icon: '🔒' },
];

export default function PlatformLandingPage() {
    const [mounted, setMounted] = useState(false);
    const branding = useBranding();

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <div className="min-h-screen bg-[#0a0a1a] text-white overflow-hidden">
            {/* Animated gradient background */}
            <div className="fixed inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/80 via-[#0a0a1a] to-purple-950/60" />
                <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '8s' }} />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
                <div className="absolute top-1/3 right-1/3 w-[300px] h-[300px] bg-cyan-500/5 rounded-full blur-[80px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '4s' }} />
            </div>

            {/* Navigation */}
            <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 max-w-7xl mx-auto">
                <div className="flex items-center gap-3">
                    {branding.logoUrl ? (
                        <img src={branding.logoUrl} alt="" className="w-10 h-10 rounded-xl object-contain" />
                    ) : (
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                            </svg>
                        </div>
                    )}
                    <span className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                        {branding.name}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <Link
                        href="/login"
                        className="px-5 py-2.5 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                        Đăng nhập
                    </Link>
                    <Link
                        href="/register"
                        className="px-5 py-2.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40"
                    >
                        Đăng ký dùng thử
                    </Link>
                </div>
            </nav>

            {/* Hero Section */}
            <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
                <div className={`pt-16 md:pt-24 pb-16 transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {/* Badge */}
                    <div className="flex justify-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-sm text-gray-400">Nền tảng quản lý phòng khám thế hệ mới</span>
                        </div>
                    </div>

                    {/* Heading */}
                    <h1 className="text-center text-4xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
                        <span className="block text-white">Quản lý phòng khám</span>
                        <span className="block mt-2 bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                            thông minh & toàn diện
                        </span>
                    </h1>

                    {/* Subtitle */}
                    <p className="mt-6 text-center text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                        Nền tảng SaaS tích hợp AI — từ tiếp nhận, khám bệnh, kê toa, xét nghiệm, 
                        quản lý tồn kho đến bán hàng, hóa đơn điện tử & tính công bác sĩ.
                    </p>

                    {/* CTA Buttons */}
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            href="/register"
                            className="group relative px-8 py-4 text-base font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 shadow-2xl shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:scale-[1.02]"
                        >
                            <span className="flex items-center gap-2">
                                Bắt đầu miễn phí
                                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </span>
                        </Link>
                        <Link
                            href="/login"
                            className="px-8 py-4 text-base font-medium text-gray-300 rounded-2xl border border-white/10 hover:bg-white/5 hover:border-white/20 transition-all duration-300"
                        >
                            Đăng nhập hệ thống
                        </Link>
                    </div>
                </div>

                {/* Stats Bar */}
                <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 pb-16 transition-all duration-1000 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {STATS.map((stat, i) => (
                        <div key={i} className="text-center p-4 md:p-5 rounded-2xl bg-white/5 border border-white/5">
                            <div className="text-2xl mb-1">{stat.icon}</div>
                            <div className="text-lg md:text-xl font-bold bg-gradient-to-r from-indigo-300 to-purple-300 bg-clip-text text-transparent">
                                {stat.value}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                        </div>
                    ))}
                </div>

                {/* Features Grid — 6 cards */}
                <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-20 transition-all duration-1000 delay-400 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {FEATURES.map((feature, i) => (
                        <div
                            key={i}
                            className="group relative p-7 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all duration-500"
                        >
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                {feature.icon}
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                        </div>
                    ))}
                </div>

                {/* Platform Highlights */}
                <div className={`pb-20 transition-all duration-1000 delay-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div className="text-center mb-10">
                        <h2 className="text-2xl md:text-3xl font-bold text-white">Tại sao chọn {branding.name}?</h2>
                        <p className="mt-3 text-gray-400 max-w-xl mx-auto">Giải pháp toàn diện, triển khai nhanh, không cần cài đặt phần mềm.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                emoji: '🚀',
                                title: 'Triển khai trong 5 phút',
                                desc: 'Đăng ký, đăng nhập và bắt đầu sử dụng ngay. Không cần cài đặt, không cần server.',
                            },
                            {
                                emoji: '🔗',
                                title: 'Tích hợp thiết bị y tế',
                                desc: 'Kết nối trực tiếp máy xét nghiệm qua Agent, nhận kết quả tự động vào hệ thống.',
                            },
                            {
                                emoji: '📱',
                                title: 'Web & Desktop App',
                                desc: 'Sử dụng trên trình duyệt hoặc ứng dụng desktop. Hỗ trợ hoạt động offline.',
                            },
                        ].map((item, i) => (
                            <div key={i} className="flex gap-4 p-6 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all duration-300">
                                <div className="text-3xl flex-shrink-0">{item.emoji}</div>
                                <div>
                                    <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                                    <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Trust indicators */}
                <div className={`text-center pb-16 transition-all duration-1000 delay-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
                    <p className="text-sm text-gray-500 mb-4">Được tin dùng bởi</p>
                    <div className="flex items-center justify-center gap-8 text-gray-600">
                        <span className="text-sm font-medium">🏥 Phòng khám BS Hy</span>
                        <span className="text-gray-700">•</span>
                        <span className="text-sm font-medium">Và nhiều phòng khám khác...</span>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="relative z-10 border-t border-white/5 py-8">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-gray-500">© 2026 ClirxOS. All rights reserved.</p>
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                        <a href="#" className="hover:text-gray-300 transition-colors">Điều khoản</a>
                        <a href="#" className="hover:text-gray-300 transition-colors">Bảo mật</a>
                        <a href="#" className="hover:text-gray-300 transition-colors">Liên hệ</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
